import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import LoadingSpinner from "../components/LoadingSpinner";
import SafeRichText from "../components/SafeRichText";
import { useCachedData, useCacheInvalidationListener } from "../utils/useCache";

const API_BASE = process.env.REACT_APP_API_BASE_URL || '/backend/php';

// Helper to format ISO date to DD/MM/YYYY format
function formatDate(dateString) {
  if (!dateString) return 'Date TBD';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Date TBD';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

// Helper to resolve an image source from an event object
function resolveImageSrc(item, apiBase) {
  if (!item) return null;
  if (item._imageSrc) return item._imageSrc;

  if (item.image_data) {
    const d = item.image_data;
    if (typeof d === 'string') {
      const trimmed = d.trim();
      if (trimmed) {
        if (trimmed.startsWith('data:')) return trimmed;
        const mime = item.image_mimetype || 'image/jpeg';
        return `data:${mime};base64,${trimmed}`;
      }
    } else if (typeof d === 'object') {
      try {
        const arr = Array.isArray(d.data) ? new Uint8Array(d.data) : new Uint8Array(d);
        const blob = new Blob([arr], { type: item.image_mimetype || 'image/jpeg' });
        return URL.createObjectURL(blob);
      } catch (err) {
        console.warn('Failed to convert image_data to blob:', err);
        // fallthrough to image_url
      }
    }
  }

  if (item.image_url) {
    const imageUrl = String(item.image_url).trim();
    
    // Skip only if explicitly empty or 'null' string
    if (!imageUrl || imageUrl === 'null' || imageUrl === 'undefined') {
      return null;
    }
    
    // absolute URL starting with http(s) - return as-is
    if (/^https?:\/\//i.test(imageUrl)) {
      return imageUrl;
    }
    
    // URLs starting with / - these are either API endpoints or absolute paths
    // Check if it looks like an API endpoint (contains /api/)
    if (imageUrl.startsWith('/api/')) {
      // It's an API endpoint, prefix with apiBase
      return `${apiBase}${imageUrl}`;
    }
    
    // Other absolute paths starting with /
    if (imageUrl.startsWith('/')) {
      return `${apiBase}${imageUrl}`;
    }
    
    // numeric id - convert to /image/id endpoint
    if (!isNaN(Number(imageUrl))) {
      return `${apiBase}/image/${imageUrl}`;
    }
    
    // relative path - prefix with apiBase
    return `${apiBase}/${imageUrl}`;
  }

  // image_id as fallback
  if (item.image_id) {
    return `${apiBase}/image/${item.image_id}`;
  }

  return null;
}

function EventDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: event, loading, error, refetch } = useCachedData(
    `/api/events/${id}`,
    () => axios.get(`${API_BASE}/api/events/${id}`).then(async (res) => {
      const { normalizeItem } = await import('../utils/responseHelpers');
      return normalizeItem(res);
    }),
    300000 // 5 minute cache
  );
  
  // Listen for cache invalidation events from admin pages and refetch
  useCacheInvalidationListener('/api/events', refetch);

  if (loading) {
    return (
      <div className="w-full max-w-8xl rounded-lg sm:rounded-xl md:rounded-2xl shadow-xl p-2 sm:p-4 md:p-8 text-base sm:text-lg overflow-x-auto" style={{ background: 'rgba(245, 247, 255, 0.85)', backdropFilter: 'blur(16px)' }}>
        <div className="min-h-screen">
          <LoadingSpinner message="Loading event details..." bgColor="bg-transparent" spinnerColor="border-[#73ab40]" py={0} />
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="w-full max-w-8xl rounded-lg sm:rounded-xl md:rounded-2xl shadow-xl p-2 sm:p-4 md:p-8 text-base sm:text-lg overflow-x-auto" style={{ background: 'rgba(245, 247, 255, 0.85)', backdropFilter: 'blur(16px)' }}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-xl font-medium text-blue-600 mb-6">This event is not available right now. Please check back later!</p>
            <motion.button
              onClick={() => navigate('/events')}
              className="px-8 py-3 bg-gradient-to-r from-primary-500 to-emerald-700 text-white font-bold rounded-2xl hover:shadow-lg transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Back to Events
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/backend/php';
  const eventImage = resolveImageSrc(event, API_BASE_URL);
  console.log(`EventDetails for ${event.id} (${event.title}):`, { image_data: event.image_data ? 'exists' : 'none', image_url: event.image_url, eventImage });

  return (
    <div className="w-full rounded-lg sm:rounded-xl md:rounded-2xl shadow-xl p-2 sm:p-4 md:p-8 text-base sm:text-lg overflow-x-auto" style={{ background: 'rgba(245, 247, 255, 0.85)', backdropFilter: 'blur(16px)' }}>
      <div className="min-h-screen relative overflow-hidden">
        <div className="fixed inset-0 bg-slate-50" />

        <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="max-w-4xl mx-auto">
            {/* Back Button */}
            <motion.button
              onClick={() => navigate('/events')}
              className="mb-8 flex items-center gap-2 px-4 py-2 bg-white/50 backdrop-blur-xl rounded-full border border-slate-200/50 text-slate-700 hover:bg-white/80 transition-all"
              whileHover={{ x: -5 }}
            >
              <span>←</span> Back to Events
            </motion.button>

            {/* Event Image */}
            <motion.div
              className="relative w-full overflow-hidden rounded-3xl mb-8 shadow-2xl"
              style={{ paddingBottom: '50%' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <img
                src={eventImage || '/images/image(1).png'}
                alt={event.title}
                className="absolute top-0 left-0 w-full h-full object-cover"
                onError={(e) => { 
                  if (!e.currentTarget.src.includes('image(1).png')) {
                    e.currentTarget.src = '/images/image(1).png';
                  }
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </motion.div>

            {/* Main Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white/60 backdrop-blur-xl rounded-3xl border border-slate-200/50 p-8 md:p-12 shadow-strong"
            >
              {/* Title */}
              <motion.h1
                className="text-5xl font-bold text-slate-900 mb-6 text-center"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                {event.title}
              </motion.h1>

              {/* Event Date */}
              <motion.div
                className="flex items-center gap-3 px-4 py-3 bg-primary-50 rounded-2xl border border-primary-200 mb-8"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <span className="text-2xl">📅</span>
                <div>
                  <p className="text-xs font-semibold text-primary-600 uppercase">Event Date</p>
                  <p className="text-slate-800 font-bold">{formatDate(event.event_date || event.date)}</p>
                </div>
              </motion.div>

              {/* Description */}
              <motion.div
                className="mb-12"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <h2 className="text-3xl font-bold text-slate-800 mb-4">About This Event</h2>
                <div className="text-lg text-slate-700 leading-relaxed">
                  {event.description 
                    ? <SafeRichText content={event.description} />
                    : event.content 
                    ? <SafeRichText content={event.content} />
                    : 'Join us for an exciting event where industry leaders and innovators gather to share insights, network, and explore emerging opportunities in technology and business development. This comprehensive event is designed to bring together professionals from various sectors to collaborate, learn, and grow.'}
                </div>
              </motion.div>

            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventDetails;
